import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FolderKanban, CheckCircle2, Users, CalendarClock, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { TIPI_PRATICA, formatDate } from "@/lib/constants";
import { useStudio } from "@/context/StudioContext";

export default function Dashboard() {
  const { settings } = useStudio();
  const practiceTypes = settings?.practice_types?.length ? settings.practice_types : TIPI_PRATICA;
  const [stats, setStats] = useState(null);

  useEffect(() => {
    API.get("/stats/dashboard").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  if (!stats) {
    return (
      <div className="flex justify-center py-24" data-testid="dashboard-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-blue-800 border-t-transparent" />
      </div>
    );
  }

  const cards = [
    { label: "Pratiche in corso", value: stats.in_corso, icon: FolderKanban, color: "text-blue-800 bg-blue-100", testid: "stat-pratiche-in-corso", href: "/pratiche?vista=attive" },
    { label: "Pratiche concluse", value: stats.concluse, icon: CheckCircle2, color: "text-emerald-700 bg-emerald-100", testid: "stat-pratiche-concluse", href: "/pratiche?vista=archivio" },
    { label: "Clienti attivi", value: stats.clienti, icon: Users, color: "text-slate-700 bg-slate-200", testid: "stat-clienti" },
    { label: "Scadenze entro 30gg", value: stats.scadenze_30gg, icon: CalendarClock, color: "text-amber-700 bg-amber-100", testid: "stat-scadenze" },
  ];

  const maxType = Math.max(1, ...Object.values(stats.by_type));

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <p className="font-mono text-xs uppercase tracking-widest text-slate-500 mb-1">Panoramica</p>
        <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-slate-900">Dashboard Studio</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {cards.map(({ label, value, icon: Icon, color, testid, href }) => {
          const Card = href ? Link : "div";
          return <Card {...(href ? {to: href} : {})} key={label} className={`bg-white rounded-xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all p-5 ${href ? "cursor-pointer hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-blue-800" : ""}`} data-testid={testid}>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-500">{label}</p>
              <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${color}`}>
                <Icon className="h-4 w-4" />
              </div>
            </div>
            <p className="font-heading text-3xl font-extrabold text-slate-900">{value}</p>
          </Card>;
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="dashboard-deadlines">
          <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Scadenze imminenti</h2>
          {stats.upcoming_deadlines.length === 0 ? (
            <p className="text-sm text-slate-500">Nessuna scadenza nei prossimi 30 giorni.</p>
          ) : (
            <ul className="divide-y divide-slate-100">
              {stats.upcoming_deadlines.map((d, i) => (
                <li key={i} className="py-3 flex items-center justify-between gap-4">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">{d.title}</p>
                    <Link to={`/pratiche/${d.practice_id}`} className="text-xs text-blue-800 hover:underline truncate block">
                      {d.protocol_code} · {d.practice_title}
                    </Link>
                  </div>
                  <span className="font-mono text-xs font-semibold px-2 py-1 rounded bg-amber-100 text-amber-800 border border-amber-200 whitespace-nowrap">
                    {formatDate(d.date)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="bg-white rounded-xl border border-slate-200/80 shadow-sm p-5 sm:p-6" data-testid="dashboard-by-type">
          <h2 className="font-heading text-base md:text-lg font-semibold text-slate-900 mb-4">Pratiche per tipologia</h2>
          <div className="space-y-3">
            {practiceTypes.map((t) => (
              <div key={t.value} className="flex items-center gap-3">
                <span className="text-xs text-slate-600 w-36 truncate">{t.label}</span>
                <div className="flex-1 h-2.5 bg-slate-100 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-blue-800 rounded-full transition-all duration-500"
                    style={{ width: `${((stats.by_type[t.value] || 0) / maxType) * 100}%` }}
                  />
                </div>
                <span className="font-mono text-xs font-semibold text-slate-700 w-6 text-right">{stats.by_type[t.value] || 0}</span>
              </div>
            ))}
          </div>
          <Link
            to="/pratiche"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-blue-800 hover:underline"
            data-testid="dashboard-goto-pratiche"
          >
            Vai all'archivio pratiche <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
